import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * @typedef {object} Session - Объект сессии пользователя.
 * @property {string} access - JWT токен доступа.
 * @property {number} expiresAt - Временная метка истечения срока действия токена.
 * @property {object} userProfile - Данные профиля пользователя.
 * @property {Array<object>} availableCompanies - Список компаний, доступных пользователю.
 * @property {number|null} [currentCompanyId] - ID текущей выбранной компании.
 */

/**
 * A Pinia store for managing the user's session state.
 * This store is intended to be a reactive, read-only representation
 * of the session for the UI. The business logic for authentication
 * is handled by the `useSession` composable.
 */
export const useSessionStore = defineStore('session', () => {
	// --- STATE ---

	/**
	 * Основной объект сессии. Содержит все постоянные данные сессии.
	 * Имеет значение `null`, если пользователь не аутентифицирован.
	 * @type {import('vue').Ref<Session|null>}
	 */
	const session = ref(null)

	/**
	 * A flag to indicate if the session has been initialized.
	 * @type {import('vue').Ref<boolean>}
	 */
	const isInitializedSession = ref(false)

	// --- GETTERS ---

	/**
	 * A computed property that returns true if the user is authenticated.
	 * @type {import('vue').ComputedRef<boolean>}
	 */
	const isAuthenticated = computed(() => !!session.value)

	/**
	 * A computed property that returns the user's profile from the session.
	 * @type {import('vue').ComputedRef<object|null>}
	 */
	const userProfile = computed(() => session.value?.userProfile ?? null)

	/**
	 * A computed property that returns the user's access token from the session.
	 * @type {import('vue').ComputedRef<string|null>}
	 */
	const accessToken = computed(() => session.value?.access ?? null)

	/**
	 * A computed property that returns the list of available companies from the session.
	 * @type {import('vue').ComputedRef<string[]>}
	 */
	const availableCompanies = computed(() => session.value?.availableCompanies ?? [])

	/**
	 * A computed property that returns the ID of the currently selected company from the session.
	 * @type {import('vue').ComputedRef<number|null>}
	 */
	const currentCompanyId = computed(() => session.value?.currentCompanyId ?? null)

	/**
	 * A computed property that dynamically checks if the user has access to the current company.
	 * @type {import('vue').ComputedRef<boolean>}
	 */
	const isSessionUserHasAccessToCurrentCompany = computed(() => {
		if (!currentCompanyId.value || !availableCompanies.value.length) {
			return false
		}
		return availableCompanies.value.some((company) => company.id === currentCompanyId.value)
	})

	// --- ACTIONS ---

	/**
	 * Sets the session state. Called by the `useSession` composable after a
	 * successful login or session initialization.
	 * @param {object|null} newSession - The new session object.
	 */
	function setSession(newSession) {
		session.value = newSession
	}

	/**
	 * Clears the session state. Called by the `useSession` composable during logout.
	 */
	function clearSession() {
		session.value = null
	}

	/**
	 * Sets the initialized flag to true.
	 */
	function setInitialized() {
		isInitializedSession.value = true
	}

	/**
	 * Sets the current company ID within the session object.
	 * @param {number|null} companyId - The new company ID.
	 */
	function setCurrentCompanyId(companyId) {
		if (session.value) {
			session.value.currentCompanyId = companyId
		}
	}

	return {
		// State
		session,
		isInitializedSession,
		// Getters
		isAuthenticated,
		userProfile,
		accessToken,
		availableCompanies,
		currentCompanyId,
		isSessionUserHasAccessToCurrentCompany,
		// Actions
		setSession,
		clearSession,
		setInitialized,
		setCurrentCompanyId,
	}
})
